import type { DevelopmentJourneysWeeklyWorkflow } from "~vue/api/generated/development-journeys";
import { logError } from "~vue/utils/logUtils";
import { type ComputedRef, onScopeDispose, type Ref, watch } from "vue";

interface InitialGenerationPollingOptions {
    weeklyWorkflow: Ref<DevelopmentJourneysWeeklyWorkflow | null>;
    hasFailed: ComputedRef<boolean>;
    isReady: Ref<boolean>;
    readWorkflow: (signal: AbortSignal) => Promise<Response>;
    reloadWorkflow: () => Promise<void>;
}

const POLL_INTERVAL_MS = 2_000;

export function useInitialGenerationPolling({ weeklyWorkflow, hasFailed, isReady, readWorkflow, reloadWorkflow }: InitialGenerationPollingOptions) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    let isActive = true;
    let hasLoggedPollError = false;

    const clearPoll = () => {
        if (timer !== undefined) {
            globalThis.clearTimeout(timer);
            timer = undefined;
        }
    };

    const scheduleInitialGenerationPoll = () => {
        clearPoll();
        if (!isActive || weeklyWorkflow.value?.phase !== "generating" || hasFailed.value || isReady.value) {
            return;
        }
        timer = globalThis.setTimeout(() => {
            timer = undefined;
            void pollInitialGeneration();
        }, POLL_INTERVAL_MS);
    };

    const pollInitialGeneration = async () => {
        if (!isActive || weeklyWorkflow.value?.phase !== "generating" || hasFailed.value || isReady.value) {
            return;
        }

        const requestController = new AbortController();
        controller = requestController;
        try {
            const response = await readWorkflow(requestController.signal);
            if (!isActive || requestController.signal.aborted) {
                return;
            }
            if (!response.ok) {
                throw new Error("Unable to load initial action generation");
            }
            const workflow = (await response.json()) as DevelopmentJourneysWeeklyWorkflow;
            if (!isActive || requestController.signal.aborted) {
                return;
            }
            hasLoggedPollError = false;
            if (weeklyWorkflow.value?.phase !== "generating") {
                return;
            }
            if (workflow.phase === "normal" || workflow.phase === "review") {
                await reloadWorkflow();
                return;
            }
            weeklyWorkflow.value = workflow;
            if (workflow.phase !== "generating") {
                isReady.value = workflow.phase === "commitment";
                return;
            }
        } catch (error) {
            if (!isActive || requestController.signal.aborted) {
                return;
            }
            // The existing generating state remains useful while a progress refresh is retried.
            if (!hasLoggedPollError) {
                logError(error);
                hasLoggedPollError = true;
            }
        } finally {
            if (controller === requestController) {
                controller = undefined;
            }
        }

        scheduleInitialGenerationPoll();
    };

    watch(
        [() => weeklyWorkflow.value?.phase, () => weeklyWorkflow.value?.generation_status, isReady],
        ([phase, generationStatus, ready]) => {
            clearPoll();
            if (phase === "generating" && generationStatus?.status !== "failed" && !ready) {
                scheduleInitialGenerationPoll();
                return;
            }
            if (phase !== "generating") {
                hasLoggedPollError = false;
            }
        },
        { immediate: true },
    );

    onScopeDispose(() => {
        isActive = false;
        clearPoll();
        controller?.abort();
    });

    return { scheduleInitialGenerationPoll };
}
